import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import DataTable, { Column } from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';
import FilterBar from '../components/ui/FilterBar';

const API = `${API_V1}/ar-invoices`;
const PAGE_SIZE = 50;

interface Invoice { id: number; invoice_number: string; customer_id: string; customer_name: string; gross_amount: number; net_amount: number; balance_due: number; lifecycle_status: string; entity_code: string; currency: string; invoice_date: string; due_date: string; recognition_method: string; revenue_recognized: number; deferred_revenue: number; version: number; source_contract_id?: number | null; source_contract_number?: string | null; }
interface Summary { total: number; created: number; sent: number; partially_paid: number; paid: number; total_outstanding: number; total_revenue_recognized: number; total_deferred_revenue: number; }
interface Aging { buckets: { current: number; '1_30': number; '31_60': number; '61_90': number; over_90: number }; total_outstanding: number; }

export default function ARInvoices() {
  const navigate = useNavigate();
  const { entity } = useGlobalEntity();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [aging, setAging] = useState<Aging | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showAging, setShowAging] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [form, setForm] = useState({ invoice_number: '', customer_id: '', customer_name: '', entity_code: 'USA', gross_amount: '', tax_amount: '0', currency: 'USD', recognition_method: 'POINT_IN_TIME' });

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) });
    if (statusFilter) p.set('lifecycle_status', statusFilter);
    try {
      const [listRes, sumRes, agingRes] = await Promise.all([
        authFetch(withEntityParam(`${API}?${p}`, entity)),
        authFetch(withEntityParam(`${API}/summary`, entity)),
        authFetch(withEntityParam(`${API}/aging`, entity)),
      ]);
      if (listRes.ok) { const d = await listRes.json(); setInvoices(d.items || []); setTotal(d.total || 0); }
      if (sumRes.ok) { setSummary(await sumRes.json()); }
      if (agingRes.ok) { setAging(await agingRes.json()); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, statusFilter, entity]);

  useEffect(() => { load(); }, [load]);

  const loadAging = async () => {
    try {
      const res = await authFetch(withEntityParam(`${API}/aging`, entity));
      if (res.ok) { setAging(await res.json()); setShowAging(true); }
    } catch { showToast('Aging load failed', false); }
  };

  const handleCreate = async () => {
    try {
      const payload = { ...form, gross_amount: parseFloat(form.gross_amount) || 0, tax_amount: parseFloat(form.tax_amount) || 0 };
      const res = await authFetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (res.ok) { showToast('Invoice created', true); setShowCreate(false); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Failed', false); }
  };

  const handleAction = async (id: number, action: string, version: number, extra?: object) => {
    try {
      const res = await authFetch(`${API}/${id}/${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ expected_version: version, ...extra }) });
      if (res.ok) { showToast(`Invoice ${action}`, true); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Action failed', false); }
  };

  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' };

  const columns: Column[] = [
    { key: 'invoice_number', label: 'Invoice #', render: (v: string) => <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{v}</span> },
    { key: 'customer_name', label: 'Customer', render: (v: string, row: Invoice) => v || row.customer_id },
    { key: 'source_contract_number', label: 'Source Contract', render: (_: any, row: Invoice) => (
      row.source_contract_id ? (
        <a
          href={`/contracts/${row.source_contract_id}`}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/contracts/${row.source_contract_id}`); }}
          style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 500, fontSize: 12 }}
          title={row.source_contract_number || `Contract #${row.source_contract_id}`}
        >
          {row.source_contract_number || `#${row.source_contract_id}`}
        </a>
      ) : <span style={{ color: 'var(--text-hint)' }}>—</span>
    ) },
    { key: 'net_amount', label: 'Net Amount', align: 'right', render: (v: number) => <span style={{ fontWeight: 600 }}>{formatAmount(v)}</span> },
    { key: 'balance_due', label: 'Balance Due', align: 'right', render: (v: number) => <span style={{ color: v > 0 ? 'var(--negative)' : 'var(--positive)' }}>{formatAmount(v)}</span> },
    { key: 'due_date', label: 'Due Date', render: (v: string) => <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{v || '-'}</span> },
    { key: 'recognition_method', label: 'Rev Method', render: (v: string) => <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{v || '-'}</span> },
    { key: 'lifecycle_status', label: 'Status', render: (v: string) => <StatusBadge status={v} /> },
    { key: 'actions', label: 'Actions', render: (_: any, inv: Invoice) => (
      <div style={{ display: 'flex', gap: 4 }}>
        {inv.lifecycle_status === 'CREATED' && <ActionButton label="Send" onClick={(e) => { e.stopPropagation(); handleAction(inv.id, 'send', inv.version); }} variant="secondary" size="sm" />}
        {inv.lifecycle_status === 'CREATED' && <ActionButton label="Cancel" onClick={(e) => { e.stopPropagation(); handleAction(inv.id, 'cancel', inv.version); }} variant="danger" size="sm" />}
        {['SENT', 'PARTIALLY_PAID'].includes(inv.lifecycle_status) && <ActionButton label="Payment" onClick={(e) => { e.stopPropagation(); const amt = prompt('Payment amount:'); if (amt) handleAction(inv.id, 'record-payment', inv.version, { payment_amount: parseFloat(amt) }); }} variant="success" size="sm" />}
        {['SENT', 'PARTIALLY_PAID', 'PAID'].includes(inv.lifecycle_status) && <ActionButton label="Void" onClick={(e) => { e.stopPropagation(); handleAction(inv.id, 'void', inv.version); }} variant="danger" size="sm" />}
      </div>
    )},
  ];

  return (
    <PageLayout
      title="AR Invoices"
      subtitle="Generate, send, and track accounts receivable invoices"
      actions={[
        { label: 'Export Aging', onClick: async () => {
          try {
            const p = new URLSearchParams({ type: 'AR', format: 'csv' });
            const resp = await authFetch(`${API_V1}/reports/aging/export?${p}`);
            if (!resp.ok) throw new Error('Export failed');
            const blob = await resp.blob();
            const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
            a.download = 'ar-aging-report.csv'; a.click(); URL.revokeObjectURL(a.href);
          } catch (e) { console.error(e); showToast('Export failed', false); }
        }, variant: 'secondary' as const },
        { label: 'Aging', onClick: loadAging, variant: 'secondary' as const },
        { label: '+ New Invoice', onClick: () => setShowCreate(true) },
      ]}
    >
      {summary && (
        <KpiGrid items={[
          { label: 'Total', value: summary.total, color: 'var(--text-primary)' },
          { label: 'Created', value: summary.created, color: 'var(--text-secondary)' },
          { label: 'Sent', value: summary.sent, color: 'var(--accent)' },
          { label: 'Partially Paid', value: summary.partially_paid, color: 'var(--warning)' },
          { label: 'Paid', value: summary.paid, color: 'var(--positive)' },
          { label: 'Outstanding', value: formatAmountCompact(summary.total_outstanding), color: 'var(--negative)' },
          { label: 'Revenue', value: formatAmountCompact(summary.total_revenue_recognized), color: 'var(--positive)' },
        ]} />
      )}

      {/* AR Aging Bar Chart */}
      {aging && (() => {
        const buckets = [
          { label: 'Current', value: aging.buckets.current || 0, color: 'var(--positive)' },
          { label: '1-30', value: aging.buckets['1_30'] || 0, color: 'var(--accent)' },
          { label: '31-60', value: aging.buckets['31_60'] || 0, color: 'var(--warning)' },
          { label: '61-90', value: aging.buckets['61_90'] || 0, color: '#f97316' },
          { label: '90+', value: aging.buckets.over_90 || 0, color: 'var(--negative)' },
        ];
        const maxVal = Math.max(...buckets.map(b => b.value), 1);
        return (
          <div style={{ marginBottom: 20, padding: '16px', background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 12, textTransform: 'uppercase' }}>AR Aging Distribution</div>
            {buckets.map(b => (
              <div key={b.label} style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ width: 50, fontSize: 11, color: 'var(--text-secondary)', flexShrink: 0 }}>{b.label}</span>
                <div style={{ flex: 1, height: 20, background: 'var(--border-subtle)', borderRadius: 3, overflow: 'hidden', marginRight: 8 }}>
                  <div style={{ width: `${(b.value / maxVal) * 100}%`, height: '100%', background: b.color, borderRadius: 3, transition: 'width 0.3s' }} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-primary)', minWidth: 70, textAlign: 'right' }}>{formatAmount(b.value)}</span>
              </div>
            ))}
          </div>
        );
      })()}

      <FilterBar filters={[
        { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: v => { setStatusFilter(v); setPage(0); }, width: 200, options: ['CREATED', 'SENT', 'PARTIALLY_PAID', 'PAID', 'VOID', 'CANCELLED'].map(s => ({ value: s, label: s })) },
      ]} />

      <DataTable
        columns={columns}
        rows={invoices}
        loading={loading}
        emptyMessage="No invoices found."
        rowKey="id"
      />

      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16 }}>
          <ActionButton label="PREV" onClick={() => setPage(Math.max(0, page - 1))} variant="secondary" size="sm" disabled={page === 0} />
          <span style={{ fontSize: 11, color: 'var(--text-secondary)', alignSelf: 'center' }}>{page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</span>
          <ActionButton label="NEXT" onClick={() => setPage(page + 1)} variant="secondary" size="sm" disabled={(page + 1) * PAGE_SIZE >= total} />
        </div>
      )}

      {/* Create Invoice Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create AR Invoice" size="lg" footer={<><button onClick={() => setShowCreate(false)} style={{ padding: '6px 16px', background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 6, cursor: 'pointer' }}>Cancel</button><button onClick={handleCreate} style={{ padding: '6px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>Create Invoice</button></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input placeholder="Invoice Number *" value={form.invoice_number} onChange={e => setForm({ ...form, invoice_number: e.target.value })} style={inputStyle} />
            <input placeholder="Customer ID *" value={form.customer_id} onChange={e => setForm({ ...form, customer_id: e.target.value })} style={inputStyle} />
          </div>
          <input placeholder="Customer Name" value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} style={inputStyle} />
          <div style={{ display: 'flex', gap: 8 }}>
            <input placeholder="Gross Amount" type="number" value={form.gross_amount} onChange={e => setForm({ ...form, gross_amount: e.target.value })} style={inputStyle} />
            <input placeholder="Tax Amount" type="number" value={form.tax_amount} onChange={e => setForm({ ...form, tax_amount: e.target.value })} style={inputStyle} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input placeholder="Entity Code" value={form.entity_code} onChange={e => setForm({ ...form, entity_code: e.target.value })} style={inputStyle} />
            <select value={form.recognition_method} onChange={e => setForm({ ...form, recognition_method: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
              <option value="POINT_IN_TIME">Point-in-Time</option>
              <option value="RATABLE">Ratable (Over Time)</option>
              <option value="MILESTONE">Milestone</option>
              <option value="USAGE">Usage-Based</option>
            </select>
          </div>
        </div>
      </Modal>

      {/* Aging Modal */}
      <Modal open={showAging} onClose={() => setShowAging(false)} title="AR Aging Analysis" footer={<button onClick={() => setShowAging(false)} style={{ padding: '6px 16px', background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 6, cursor: 'pointer' }}>Close</button>}>
        {aging && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ textAlign: 'center', fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>Total Outstanding: {formatAmount(aging.total_outstanding)}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
              {[
                { label: 'Current', value: aging.buckets.current, color: 'var(--positive)' },
                { label: '1-30 Days', value: aging.buckets['1_30'], color: 'var(--accent)' },
                { label: '31-60 Days', value: aging.buckets['31_60'], color: 'var(--warning)' },
                { label: '61-90 Days', value: aging.buckets['61_90'], color: '#f97316' },
                { label: '90+ Days', value: aging.buckets.over_90, color: 'var(--negative)' },
              ].map(b => (
                <div key={b.label} style={{ textAlign: 'center', padding: '12px 8px', background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: b.color }}>{formatAmount(b.value)}</div>
                  <div style={{ fontSize: 9, color: 'var(--text-secondary)', marginTop: 4 }}>{b.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}
